import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { VideoCompilerCompilationStatus } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';

interface Props {
  onClose: VoidFunction;
  compilationId: number;
}

type FormDataT = {
  title: string;
  description: string;
  channel_id: number | null;
};

const YoutubeVideoForm: FC<Props> = ({ onClose, compilationId }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const { data: channels } = useQuery({
    queryFn: () =>
      api.get<never, Array<{ id: number; name: string }>>(
        `/v1/video-compiler/channels`,
      ),
    queryKey: ['video_compiler_yt_channels'],
    select: (data) => data.map((c) => ({ label: c.name, value: c.id })),
  });

  const {
    handleSubmit,
    clearErrors,
    control,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      title: '',
      description: '',
      channel_id: null,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(async (data) => {
      try {
        await api.post(
          `/v1/video-compiler/compilations/${compilationId}/videos`,
          data,
        );

        await queryClient.invalidateQueries([
          'video_compiler_compilations',
          {
            status: VideoCompilerCompilationStatus.COMPLETED,
          },
        ]);

        onClose();

        alerts.success('Youtube video upload started');
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading="New Youtube video" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField
                control={control}
                name="title"
                label="Video title"
                placeholder="Add title"
              />
              <FormField
                control={control}
                name="description"
                label="Video description"
                placeholder="Add description"
                type="textarea"
              />
              <FormField
                control={control}
                name="channel_id"
                label="Channel"
                type="select"
                options={channels ?? []}
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Save
            </Button>
            <Button type="button" color="secondary" onClick={onClose}>
              Close
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default YoutubeVideoForm;
